import React from 'react'
import { graphql } from '@apollo/client/react/hoc'
import { flowRight } from 'lodash'
import { useTranslation } from 'react-i18next'
import { withRouter } from 'react-router-dom'
import { withErrorBoundaryNoRender } from 'lib/errorBoundary'
import { fetchPolicy } from 'lib/apolloFetchPolicy'
import { getLocale } from 'lib/utils/getLocale'

import ControlledModal from 'components/Modal/ControlledModal'
import StatusMessage from 'components/StatusMessage'
import VehicleProfileLogic from 'components/Vehicles/VehicleProfile/VehicleProfileLogic'
import Button from 'components/UI/Button'
import ErrorComponent from 'components/Errors/ErrorComponent'
import LoadingIndicator from 'components/LoadingIndicator'

import getVehicleDeleteModal from 'graphql/view/getVehicleDeleteModal.gql'

const VehicleDeleteModal = ({ history, data }) => {
    const { t, i18n } = useTranslation(['vehicleProfile', 'vehicles'])
    const { loading, error, vehicle } = data

    return (
        <ControlledModal
            routedModal
            name="vehicle-delete-modal"
            context="vehicleDeleteModal"
            initialTitle={t('vehicles:actions.deleteVehicle.cta')}
            requireUserInput={false}
        >
            {(modalProps) => {
                if (error) return <ErrorComponent />
                if (loading) return <LoadingIndicator />

                const deleteInfo = t('vehicles:actions.deleteVehicle.deleteConfirmation')

                return (
                    <VehicleProfileLogic>
                        {(vehicleLogic) => {
                            if (vehicleLogic.error) return <ErrorComponent error={vehicleLogic.error} container={false} />
                            if (vehicleLogic.loading) return <LoadingIndicator />

                            return (
                                <StatusMessage
                                    cssClass={vehicleLogic.loading && 'loading'}
                                    type="alert"
                                    icon="icon_alert_circle_yellow"
                                    vertical
                                >
                                    <div className="info">{deleteInfo}</div>

                                    <div className="action-wrapper">
                                        <Button
                                            trackName="vehicleDelete"
                                            className="button button--delete"
                                            onClick={() => {
                                                vehicleLogic.deleteVehicle(vehicle.id, false)
                                                    .then(() => {
                                                        modalProps.closeModal()
                                                        history.push(`${getLocale(i18n.language) || '/'}`)
                                                    })
                                                    .catch(() => {})
                                            }}
                                            type="button"
                                        >
                                            {t('vehicles:actions.deleteVehicle.ctaConfirm')}
                                        </Button>
                                        <Button
                                            trackName="cancel"
                                            className="button button--secondary"
                                            onClick={() => modalProps.closeModal()}
                                            type="button"
                                        >
                                            {t('common:cancel')}
                                        </Button>
                                    </div>
                                </StatusMessage>
                            )
                        }}
                    </VehicleProfileLogic>
                )
            }}
        </ControlledModal>
    )
}

export default flowRight(
    withErrorBoundaryNoRender,
    withRouter,
    graphql(getVehicleDeleteModal, {
        options: ({ match, currentUser }) => ({
            variables: {
                vehicleId: match.params.vehicleId,
            },
            fetchPolicy: fetchPolicy.CACHE_FIRST,
        })
    }),
)(VehicleDeleteModal)
